import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import useAuth from 'src/hooks/useAuth';
import {isProd} from 'src/lib/env';
import {redactUrl, redactUrlsInRecord} from 'src/lib/redactUrl';
import {isUpstreamEmail} from 'src/lib/userUtils';

// Set this to true if you want to capture all events of yourself.
const DEV_TEST_MODE: boolean = false;

/*
We authenticate some requests with a query param (see redactUrl), and the SDK attaches URLs to
breadcrumbs, spans and the event's request context by default. Strip the credentials on the way
out so they never reach the Sentry ingest.
*/
function redactEventUrls<T extends Sentry.Event>(event: T): T {
  if (event.request) {
    event.request.url = redactUrl(event.request.url);
    event.request.query_string = redactUrl(event.request.query_string);
    // `Referer` comes from document.referrer, which carries the previous page's query string.
    event.request.headers = redactUrlsInRecord(
      event.request.headers
    ) as typeof event.request.headers;
  }

  // Replay events list every page URL the session visited.
  const withUrls = event as T & {urls?: unknown};
  if (Array.isArray(withUrls.urls)) {
    withUrls.urls = withUrls.urls.map(redactUrl);
  }

  event.transaction = redactUrl(event.transaction);

  event.breadcrumbs = event.breadcrumbs?.map((breadcrumb) => ({
    ...breadcrumb,
    message: redactUrl(breadcrumb.message),
    data: redactUrlsInRecord(breadcrumb.data),
  }));

  event.spans = event.spans?.map((span) => ({
    ...span,
    description: redactUrl(span.description),
    data: redactUrlsInRecord(span.data) as typeof span.data,
  }));

  const trace = event.contexts?.trace;
  if (trace) {
    trace.data = redactUrlsInRecord(trace.data);
  }

  return event;
}

export function initSentry() {
  if (isProd || DEV_TEST_MODE) {
    Sentry.init({
      dsn: 'https://f4843c2715984326a52c3801513d2301@o198815.ingest.sentry.io/6049898',
      environment: import.meta.env.NODE_ENV,
      release: import.meta.env.GIT_SHA,
      tracesSampler: (samplingContext) => {
        if (samplingContext.parentSampled !== undefined) {
          return samplingContext.parentSampled;
        }
        const userEmail = Sentry.getCurrentScope().getUser()?.email;

        // Always send sessions for Upstream users to Sentry to make it easier to debug
        // issues that G&P/etc see.
        if (isUpstreamEmail(userEmail || null)) {
          return 1;
        }
        return 0.2; // default is 0.2
      },

      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      // Session Replay
      // Sample 10% of non-error sessions and 100% of error sessions
      replaysSessionSampleRate: DEV_TEST_MODE ? 1.0 : 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeBreadcrumb: (breadcrumb) => ({
        ...breadcrumb,
        message: redactUrl(breadcrumb.message),
        data: redactUrlsInRecord(breadcrumb.data),
      }),
      beforeSendSpan: (span) => ({
        ...span,
        description: redactUrl(span.description),
        data: redactUrlsInRecord(span.data) as typeof span.data,
      }),
      beforeSend: (event, hint) => {
        // Filter out alerts handled by the 404 page.
        const originalException: any = hint?.originalException;

        if (!originalException || typeof originalException === 'string') {
          return event;
        }

        if (originalException.message === 'Not found') {
          return null;
        }

        // Filter out noisy non actionable errors
        const exceptions: string[] = [
          'Failed to fetch',
          'Non-Error promise rejection captured with value',
        ];
        if (exceptions.some((exception) => originalException.message?.includes(exception))) {
          return null;
        }

        return event;
      },
    });

    /*
    An event processor rather than beforeSend: session replay events carry the page URL (in
    `urls` and in the request context) and are sent on a path that never calls beforeSend.
    */
    Sentry.getGlobalScope().addEventProcessor(redactEventUrls);
  }
}

/**
 * Ensures that the current user / email is sent in the Sentry scope.
 */
export const SentryUserListener: React.FunctionComponent = () => {
  const auth = useAuth();

  const profile = auth.status === 'logged in' && auth.userProfile;
  const organization = auth.status === 'logged in' && auth.currentOrganization;

  React.useEffect(() => {
    if (!profile) {
      return;
    }

    const email = profile.email;
    const id = profile.id;
    const organizationKey = organization && organization.id;
    const organizationName = organization ? organization.name : 'organization not set';

    Sentry.setUser({email, id});
    Sentry.setExtra('organization id', organizationKey);
    Sentry.setExtra('organization name', organizationName);

    return () => {
      Sentry.getCurrentScope().clear();
    };
  }, [profile, organization]);

  return null;
};

export const SentrySessionReplayListener: React.FunctionComponent = () => {
  const auth = useAuth();
  const profile = auth.status === 'logged in' && auth.userProfile;

  React.useEffect(() => {
    if (!profile) {
      return;
    }

    // Sentry claims you can access the active replay instance from anywhere in your code
    // via .getReplay(), so let's exit if it finds one to head off the error that gets thrown
    // when multiple instances attempt to be created, since sometimes we can't prevent that.
    // (we'll handle that error below just in case.)
    const replay = Sentry.getReplay();
    if (replay) {
      return;
    }

    const email = profile.email;

    // Don't record replay sessions of ourselves, unless we are testing
    if (!isUpstreamEmail(email) || DEV_TEST_MODE) {
      try {
        const integration = Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          // This is the default but I'm setting just to explicitly call out here
          // that we aren't recording any user inputs
          maskAllInputs: true,
          /*
          Replay records resource-timing and navigation frames on its own path, so the
          breadcrumb and event masking in initSentry doesn't reach them. Each frame keeps its
          URL in payload.description.
          */
          beforeAddRecordingEvent: (recordingEvent) => {
            const payload = (recordingEvent.data as {payload?: {description?: unknown}} | undefined)
              ?.payload;
            if (payload && typeof payload.description === 'string') {
              payload.description = redactUrl(payload.description);
            }
            return recordingEvent;
          },
        });
        Sentry.addIntegration(integration);
      } catch (e) {
        // don't blow up the page for the user if adding a replay instance errors,
        // but do let Sentry know
        Sentry.captureException(e);
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
